import { fetch } from 'owa-fetch';
import { makeServiceRequest } from 'owa-service/lib/ServiceRequest';
import { buildAuthorizationHeader } from 'owa-tokenprovider-utils';
import getAccessToken from './getAccessToken';
import type { AuthTokenResponse } from 'owa-tokenprovider';
import getGuid from 'owa-service/lib/getGuid';
import { getApp, getHostedUxEcsFilter, getSessionId } from 'owa-config';
import {
    correlationVectorHeaderName,
    getCorrelationVector,
} from 'owa-service/lib/correlationVector';
import { setCanaryHeader } from 'owa-service/lib/setCanaryHeader';
import { addIdTypePreference } from 'owa-service/lib/addIdTypePreference';
import { addIncludeThirdPartyOnlineMeetingProvidersHeader } from 'owa-service/lib/addIncludeThirdPartyOnlineMeetingProvidersHeader';

type BookingsServiceRequestParameters = {
    endpointPrefix: string;
    smtpAddress: string;
    action: string;
    isAuthRequired: boolean;
    returnFullResponseOnSuccess?: boolean;
    method?: string;
    /* eslint-disable-next-line owa-custom-rules/no-optional-any-parameter -- (https://aka.ms/OWALintWiki)
     * DO NOT COPY-PASTE! This code should be fixed by any developer touching this code
     *	> Optional object properties should not have type "any". This can hide undefined/null references otherwise detectable by the transpiler. */
    body?: any;
    bwmHeader?: boolean;
    queryString?: string;
    requestHeaders?: Headers;
    retryCount?: number;
};

const makeBookingsServiceRequest = async (
    requestParameters: BookingsServiceRequestParameters
): Promise<any> => {
    try {
        const {
            endpointPrefix,
            smtpAddress,
            action,
            isAuthRequired,
            returnFullResponseOnSuccess = false,
            method = 'GET',
            body = {},
            bwmHeader = false,
            queryString = '',
            requestHeaders,
            retryCount,
        } = requestParameters;
        const queryStringParams = queryString ? `?${queryString}` : '';
        const endpoint = `${endpointPrefix}/${smtpAddress}/${action}${queryStringParams}`;
        const headers = new Headers(requestHeaders);
        headers.append('X-AnchorMailbox', smtpAddress);

        if (bwmHeader) {
            headers.append('X-ClientInfo', 'bwmC2');
        }
        if (isAuthRequired) {
            let token: string | AuthTokenResponse | undefined;
            const [authToken, tokenPromise] = getAccessToken();
            if (!authToken) {
                token = await tokenPromise;
            } else {
                token = authToken;
            }
            if (token) {
                headers.append('Authorization', buildAuthorizationHeader(token as string));
            }
            return makeServiceRequest<any>(action, body, {
                headers,
                method,
                endpoint,
                returnFullResponseOnSuccess,
                retryCount,
                noEmptyPost: method === 'POST' || method === 'PUT' || method === 'PATCH',
            });
        } else {
            // This change is to call B2 APIs without auth token
            // With MSAL changes in place, we are not able to get the token for B2 APIs with makeServiceRequest method
            // Hence, we are using fetch method directly with necessary headers to call B2 APIs
            headers.append('Content-Type', 'application/json; charset=utf-8');
            headers.set('X-OWA-CorrelationId', getGuid());
            headers.set('X-OWA-SessionId', getSessionId());
            headers.set('X-Req-Source', getApp());
            headers.set('x-owa-hosted-ux', getHostedUxEcsFilter());

            setCanaryHeader(headers);
            const cv = getCorrelationVector();
            // the cv can be falsy if it gets corrupted, in that case there is no point in sending it
            if (cv) {
                headers.append(correlationVectorHeaderName, cv);
            }

            addIdTypePreference(headers, 'ImmutableId');
            addIncludeThirdPartyOnlineMeetingProvidersHeader(headers);
            return fetch(endpoint, {
                method,
                headers,
                body: method == 'GET' ? undefined : JSON.stringify(body),
            });
        }
    } catch (error) {
        return error;
    }
};

export default makeBookingsServiceRequest;
